// Epoch 19: Lead Generator Enhancements - Admin Pricing Configuration Component
// File: src/components/admin/AdminPricingConfiguration.tsx
// Purpose: Admin UI for configuring lead pricing and tier definitions

import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';
import BasePricingEditor from './BasePricingEditor';
import TierPricingEditor from './TierPricingEditor';
import TierRangeEditor from './TierRangeEditor';

interface PricingConfig {
  id: string;
  config_type: 'tier_pricing' | 'base_pricing' | 'tier_ranges';
  config_data: BasePricingData | TierPricingData | TierRangesData;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

interface BasePricingData {
  base_price_per_1000: number;
  max_leads_per_export: number;
  currency: string;
}

interface TierPricingData {
  tier_upcharges: {
    [key: string]: number;
  };
}

interface TierRangesData {
  tier_score_ranges: {
    [key: string]: {
      min: number;
      max: number;
    };
  };
}

export default function AdminPricingConfiguration() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<'base' | 'tiers' | 'ranges'>('base');

  // Configuration state
  const [basePricing, setBasePricing] = useState<BasePricingData>({
    base_price_per_1000: 50,
    max_leads_per_export: 1000,
    currency: 'USD'
  });

  const [tierPricing, setTierPricing] = useState<TierPricingData>({
    tier_upcharges: {
      '1': 0.10,
      '2': 0.08,
      '3': 0.05,
      '4': 0.03,
      '5': 0.01
    }
  });

  const [tierRanges, setTierRanges] = useState<TierRangesData>({
    tier_score_ranges: {
      '1': { min: 90, max: 100 },
      '2': { min: 80, max: 89 },
      '3': { min: 70, max: 79 },
      '4': { min: 60, max: 69 },
      '5': { min: 0, max: 59 }
    }
  });

  // Load existing configuration
  useEffect(() => {
    loadPricingConfiguration();
  }, []);

  const loadPricingConfiguration = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: configs, error: fetchError } = await supabase
        .from('pricing_config')
        .select('*')
        .eq('is_active', true)
        .order('updated_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      // Process configurations
      configs?.forEach((config: PricingConfig) => {
        switch (config.config_type) {
          case 'base_pricing':
            setBasePricing(config.config_data as BasePricingData);
            break;
          case 'tier_pricing':
            setTierPricing(config.config_data as TierPricingData);
            break;
          case 'tier_ranges':
            setTierRanges(config.config_data as TierRangesData);
            break;
        }
      });
    } catch (err) {
      console.error('Error loading pricing configuration:', err);
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async (
    configType: 'base_pricing' | 'tier_pricing' | 'tier_ranges',
    configData: BasePricingData | TierPricingData | TierRangesData
  ) => {
    setSaving(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Authentication required');
      }

      // Deactivate existing configuration of this type
      await supabase
        .from('pricing_config')
        .update({ is_active: false })
        .eq('config_type', configType);

      // Insert new configuration
      const { error: insertError } = await supabase
        .from('pricing_config')
        .insert({
          config_type: configType,
          config_data: configData,
          created_by: user.id,
          is_active: true
        });

      if (insertError) {
        throw insertError;
      }

      // Show success message
      const sections = {
        base_pricing: 'Base Pricing',
        tier_pricing: 'Tier Pricing',
        tier_ranges: 'Tier Ranges'
      };
      
      alert(`${sections[configType]} configuration saved successfully!`);
    } catch (err) {
      console.error('Error saving configuration:', err);
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleBasePricingChange = (newBasePricing: BasePricingData) => {
    setBasePricing(newBasePricing);
  };

  const handleTierPricingChange = (newTierPricing: TierPricingData) => {
    setTierPricing(newTierPricing);
  };

  const handleTierRangesChange = (newTierRanges: TierRangesData) => {
    setTierRanges(newTierRanges);
  };

  const handleSaveSection = async () => {
    switch (activeSection) {
      case 'base':
        await saveConfiguration('base_pricing', basePricing);
        break;
      case 'tiers':
        await saveConfiguration('tier_pricing', tierPricing);
        break;
      case 'ranges':
        await saveConfiguration('tier_ranges', tierRanges);
        break;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pricing configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Pricing Configuration</h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure lead pricing, tier upcharges, and score ranges
            </p>
          </div>
          <button
            onClick={handleSaveSection}
            disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Section Navigation */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex space-x-1">
          {[
            { key: 'base', label: 'Base Pricing', icon: '💰' },
            { key: 'tiers', label: 'Tier Pricing', icon: '📊' },
            { key: 'ranges', label: 'Score Ranges', icon: '🎯' }
          ].map((section) => (
            <button
              key={section.key}
              onClick={() => setActiveSection(section.key as 'base' | 'tiers' | 'ranges')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 ${
                activeSection === section.key
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-white'
              }`}
            >
              <span>{section.icon}</span>
              <span>{section.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Configuration Sections */}
      <div className="p-6">
        {activeSection === 'base' && (
          <BasePricingEditor
            basePricing={basePricing}
            onChange={handleBasePricingChange}
          />
        )}

        {activeSection === 'tiers' && (
          <TierPricingEditor
            tierPricing={tierPricing}
            onChange={handleTierPricingChange}
          />
        )}

        {activeSection === 'ranges' && (
          <TierRangeEditor
            tierRanges={tierRanges}
            onChange={handleTierRangesChange}
          />
        )}
      </div>

      {/* Footer with Important Notes */}
      <div className="px-6 py-4 bg-yellow-50 border-t border-yellow-200">
        <div className="flex items-start space-x-2">
          <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <p className="text-sm text-yellow-800 font-medium">Important Configuration Notes</p>
            <ul className="text-xs text-yellow-700 mt-1 list-disc list-inside space-y-1">
              <li>Changes take effect immediately for new lead exports</li>
              <li>Existing exported leads are not affected by pricing changes</li>
              <li>Tier score ranges should not overlap and must cover 0-100</li>
              <li>All changes are logged with timestamps and user attribution</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}